/**
 * LABELED SAMPLE DATA — served only behind VITE_USE_FIXTURES=true (offline
 * dev) or for the Home dashboard's still-unbacked widgets (KPI strip,
 * incidents, security rollup, tenant movements, journeys board — see the
 * `sampleData` badge on those widgets, ADR-0002 D4). Never served as live.
 *
 * The catalog/coverage data below is not invented: team ownership mirrors
 * internal/org/seed/seed.go's real serviceTeam/serviceName maps, and the
 * coverage counts mirror docs/design/coverage.json (the real specload output
 * over docs/design/services/*.md) — so switching VITE_USE_FIXTURES off keeps
 * the same shape and roughly the same numbers as the live store.
 */
import type {
  Alert,
  AlertQualityRow,
  CoverageRow,
  Incident,
  IncidentDetail,
  InventoryItem,
  KBServiceDetail,
  Lens,
  Notification,
  OrgAgent,
  OrgPerson,
  SearchResult,
  ServiceDetail,
  ServiceHealthView,
  ServiceSummary,
  Silence,
  Sop,
  SopCoverageRow,
  SopDetail,
  TimelineEntry,
  WhoAmIEnvelope,
} from './types'

// ---------------------------------------------------------------------------
// Fleet catalog + coverage — real ground truth (seed.go team map, coverage.json)
// ---------------------------------------------------------------------------

/** serviceKey -> provisional owning team key, verbatim from internal/org/seed/seed.go. */
const TEAM_KEYS: Record<string, string> = {
  compute: 'compute',
  gke: 'compute',
  cloudrun: 'compute',
  cloudfunctions: 'compute',
  autoscaler: 'compute',
  disks: 'compute',
  cloudsql: 'data-platform',
  alloydb: 'data-platform',
  spanner: 'data-platform',
  bigtable: 'data-platform',
  bigquery: 'data-platform',
  dataflow: 'data-platform',
  datafusion: 'data-platform',
  dataplex: 'data-platform',
  dataproc: 'data-platform',
  composer: 'data-platform',
  firestore: 'data-platform',
  memorystore: 'data-platform',
  looker: 'data-platform',
  objectstore: 'storage',
  filestore: 'storage',
  backupdr: 'storage',
  artifactregistry: 'storage',
  network: 'networking',
  loadbalancer: 'networking',
  dns: 'networking',
  cloudnat: 'networking',
  firewall: 'networking',
  cloudarmor: 'networking',
  kms: 'security',
  secretmanager: 'security',
  privateca: 'security',
  iam: 'security',
  scc: 'security',
  audit: 'security',
  compliance: 'security',
  residency: 'security',
  dashboards: 'observability',
  logging: 'observability',
  trace: 'observability',
  pubsub: 'messaging-integration',
  cloudtasks: 'messaging-integration',
  cloudscheduler: 'messaging-integration',
  eventarc: 'messaging-integration',
  workflows: 'messaging-integration',
  apigateway: 'messaging-integration',
  apigee: 'messaging-integration',
  cloudbuild: 'platform-devex',
  resourcemanager: 'platform-devex',
  billing: 'platform-devex',
}

/** serviceKey -> display name, verbatim from internal/org/seed/seed.go. */
const SERVICE_NAMES: Record<string, string> = {
  alloydb: 'Enterprise PostgreSQL (AlloyDB-equivalent)',
  apigateway: 'API Gateway (Envoy control plane)',
  apigee: 'API Management (Apigee-compatible)',
  artifactregistry: 'Artifact Registry',
  audit: 'Audit',
  autoscaler: 'Autoscaler',
  backupdr: 'Backup & DR',
  bigquery: 'Data Warehouse (formerly BigQuery)',
  bigtable: 'Bigtable (Managed Wide-Column Store)',
  billing: 'Billing',
  cloudarmor: 'Cloud Armor (WAF + DDoS edge perimeter)',
  cloudbuild: 'Cloud Build',
  cloudfunctions: 'Cloud Functions (Gen2 FaaS)',
  cloudnat: 'NAT Gateway (Cloud NAT)',
  cloudrun: 'Serverless Containers',
  cloudscheduler: 'Cloud Scheduler',
  cloudsql: 'Cloud SQL (PostgreSQL)',
  cloudtasks: 'Cloud Tasks',
  compliance: 'Compliance',
  composer: 'Composer (managed workflow orchestration)',
  compute: 'Compute (VM Instances)',
  dashboards: 'Dashboards',
  dataflow: 'Dataflow',
  datafusion: 'Data Fusion',
  dataplex: 'Dataplex (Data Governance)',
  dataproc: 'Dataproc (Managed Spark)',
  disks: 'Persistent Disks (Block Storage)',
  dns: 'DNS (Cloud DNS)',
  eventarc: 'EventVerse (trigger routing)',
  filestore: 'Filestore (managed NFS)',
  firestore: 'Document Store (Firestore-shape)',
  firewall: 'Firewall',
  gke: 'GKE (Kubernetes orchestration)',
  iam: 'IAM',
  kms: 'KMS (Key Management Service)',
  loadbalancer: 'Load Balancer',
  logging: 'Logging',
  looker: 'Looker (Business Intelligence)',
  memorystore: 'Memorystore (In-Memory Store)',
  network: 'Network (VPC)',
  objectstore: 'Object Store',
  privateca: 'Private CA',
  pubsub: 'Pub/Sub (Pulsar)',
  residency: 'Residency (Data Residency / Sovereignty)',
  resourcemanager: 'Resource Manager (org / folder / project hierarchy)',
  scc: 'Security Center',
  secretmanager: 'Secret Manager',
  spanner: 'Distributed SQL (formerly Spanner)',
  trace: 'Trace',
  workflows: 'Workflows',
}

/** serviceKey -> archetype, best-effort (UI-only label; the live spec's own §1 archetype line is authoritative). */
const ARCHETYPES: Record<string, string> = {
  iam: 'identity',
  kms: 'security',
  secretmanager: 'security',
  resourcemanager: 'orchestration',
  billing: 'analytics',
  audit: 'observability',
  logging: 'observability',
  dns: 'networking',
  pubsub: 'messaging',
  objectstore: 'object-storage',
  cloudsql: 'relational-db',
  bigquery: 'analytics',
  bigtable: 'wide-column-store',
  spanner: 'relational-db',
  firestore: 'document-db',
  memorystore: 'cache',
  trace: 'observability',
  network: 'networking',
  firewall: 'networking',
  disks: 'storage',
  compute: 'compute',
  loadbalancer: 'networking',
  cloudnat: 'networking',
  autoscaler: 'orchestration',
  artifactregistry: 'serverless',
  gke: 'orchestration',
  cloudbuild: 'serverless',
  cloudrun: 'serverless',
  cloudfunctions: 'serverless',
  residency: 'compliance',
  backupdr: 'orchestration',
  privateca: 'security',
  cloudarmor: 'security',
  scc: 'security',
  compliance: 'compliance',
  dashboards: 'observability',
  cloudtasks: 'messaging',
  eventarc: 'messaging',
  cloudscheduler: 'orchestration',
  filestore: 'storage',
  workflows: 'workflow-orchestration',
  alloydb: 'relational-db',
  apigateway: 'api-gateway',
  apigee: 'api-gateway',
  dataflow: 'data-pipeline',
  dataproc: 'data-pipeline',
  composer: 'workflow-orchestration',
  dataplex: 'data-pipeline',
  datafusion: 'data-pipeline',
  looker: 'bi',
}

const ENVIRONMENTS = ['prod', 'staging', 'dev']

/** Real specload output — mirrors docs/design/coverage.json verbatim, already worst-first. */
export const FIXTURE_COVERAGE: CoverageRow[] = [
  { service: 'dataproc', wired: 3, caveat: 17, gap: 26, total: 46, ratio: 0.06521739130434782 },
  { service: 'dataplex', wired: 5, caveat: 6, gap: 26, total: 37, ratio: 0.13513513513513514 },
  { service: 'composer', wired: 7, caveat: 14, gap: 25, total: 46, ratio: 0.15217391304347827 },
  { service: 'residency', wired: 6, caveat: 10, gap: 17, total: 33, ratio: 0.18181818181818182 },
  { service: 'dataflow', wired: 8, caveat: 15, gap: 17, total: 40, ratio: 0.2 },
  { service: 'looker', wired: 8, caveat: 11, gap: 20, total: 39, ratio: 0.20512820512820512 },
  { service: 'cloudrun', wired: 9, caveat: 14, gap: 20, total: 43, ratio: 0.20930232558139536 },
  { service: 'privateca', wired: 5, caveat: 5, gap: 13, total: 23, ratio: 0.21739130434782608 },
  { service: 'resourcemanager', wired: 10, caveat: 14, gap: 20, total: 44, ratio: 0.22727272727272727 },
  { service: 'datafusion', wired: 9, caveat: 12, gap: 18, total: 39, ratio: 0.23076923076923078 },
  { service: 'backupdr', wired: 11, caveat: 12, gap: 22, total: 45, ratio: 0.24444444444444444 },
  { service: 'memorystore', wired: 12, caveat: 15, gap: 20, total: 47, ratio: 0.2553191489361702 },
  { service: 'cloudnat', wired: 9, caveat: 12, gap: 14, total: 35, ratio: 0.2571428571428571 },
  { service: 'firestore', wired: 12, caveat: 9, gap: 24, total: 45, ratio: 0.26666666666666666 },
  { service: 'alloydb', wired: 11, caveat: 10, gap: 20, total: 41, ratio: 0.2682926829268293 },
  { service: 'objectstore', wired: 10, caveat: 14, gap: 13, total: 37, ratio: 0.2702702702702703 },
  { service: 'spanner', wired: 12, caveat: 11, gap: 21, total: 44, ratio: 0.2727272727272727 },
  { service: 'cloudfunctions', wired: 11, caveat: 16, gap: 12, total: 39, ratio: 0.28205128205128205 },
  { service: 'compute', wired: 14, caveat: 13, gap: 22, total: 49, ratio: 0.2857142857142857 },
  { service: 'iam', wired: 9, caveat: 16, gap: 6, total: 31, ratio: 0.2903225806451613 },
  { service: 'loadbalancer', wired: 9, caveat: 6, gap: 16, total: 31, ratio: 0.2903225806451613 },
  { service: 'artifactregistry', wired: 13, caveat: 13, gap: 18, total: 44, ratio: 0.29545454545454547 },
  { service: 'scc', wired: 13, caveat: 10, gap: 21, total: 44, ratio: 0.29545454545454547 },
  { service: 'audit', wired: 7, caveat: 7, gap: 9, total: 23, ratio: 0.30434782608695654 },
  { service: 'secretmanager', wired: 7, caveat: 4, gap: 12, total: 23, ratio: 0.30434782608695654 },
  { service: 'trace', wired: 13, caveat: 7, gap: 22, total: 42, ratio: 0.30952380952380953 },
  { service: 'dns', wired: 15, caveat: 16, gap: 17, total: 48, ratio: 0.3125 },
  { service: 'cloudscheduler', wired: 11, caveat: 9, gap: 15, total: 35, ratio: 0.3142857142857143 },
  { service: 'cloudsql', wired: 13, caveat: 9, gap: 19, total: 41, ratio: 0.3170731707317073 },
  { service: 'cloudbuild', wired: 15, caveat: 5, gap: 27, total: 47, ratio: 0.3191489361702128 },
  { service: 'bigtable', wired: 15, caveat: 16, gap: 15, total: 46, ratio: 0.32608695652173914 },
  { service: 'cloudarmor', wired: 11, caveat: 6, gap: 16, total: 33, ratio: 0.3333333333333333 },
  { service: 'billing', wired: 13, caveat: 12, gap: 13, total: 38, ratio: 0.34210526315789475 },
  { service: 'compliance', wired: 7, caveat: 4, gap: 9, total: 20, ratio: 0.35 },
  { service: 'filestore', wired: 15, caveat: 7, gap: 20, total: 42, ratio: 0.35714285714285715 },
  { service: 'network', wired: 14, caveat: 10, gap: 14, total: 38, ratio: 0.3684210526315789 },
  { service: 'bigquery', wired: 20, caveat: 22, gap: 11, total: 53, ratio: 0.37735849056603776 },
  { service: 'eventarc', wired: 17, caveat: 8, gap: 20, total: 45, ratio: 0.37777777777777777 },
  { service: 'pubsub', wired: 17, caveat: 11, gap: 17, total: 45, ratio: 0.37777777777777777 },
  { service: 'dashboards', wired: 14, caveat: 3, gap: 20, total: 37, ratio: 0.3783783783783784 },
  { service: 'logging', wired: 18, caveat: 13, gap: 16, total: 47, ratio: 0.3829787234042553 },
  { service: 'autoscaler', wired: 17, caveat: 10, gap: 17, total: 44, ratio: 0.38636363636363635 },
  { service: 'disks', wired: 16, caveat: 7, gap: 18, total: 41, ratio: 0.3902439024390244 },
  { service: 'cloudtasks', wired: 18, caveat: 10, gap: 17, total: 45, ratio: 0.4 },
  { service: 'workflows', wired: 17, caveat: 7, gap: 18, total: 42, ratio: 0.40476190476190477 },
  { service: 'gke', wired: 10, caveat: 5, gap: 9, total: 24, ratio: 0.4166666666666667 },
  { service: 'apigateway', wired: 20, caveat: 6, gap: 18, total: 44, ratio: 0.45454545454545453 },
  { service: 'firewall', wired: 19, caveat: 9, gap: 13, total: 41, ratio: 0.4634146341463415 },
  { service: 'apigee', wired: 21, caveat: 9, gap: 14, total: 44, ratio: 0.4772727272727273 },
  { service: 'kms', wired: 14, caveat: 6, gap: 8, total: 28, ratio: 0.5 },
]

const coverageByService = new Map(FIXTURE_COVERAGE.map((r) => [r.service, r]))

export const FIXTURE_SERVICES: ServiceSummary[] = Object.keys(SERVICE_NAMES)
  .sort()
  .map((key) => {
    const cov = coverageByService.get(key)
    return {
      key,
      name: SERVICE_NAMES[key],
      archetype: ARCHETYPES[key],
      owners: [TEAM_KEYS[key]],
      environments: ENVIRONMENTS,
      // health is never guessed (ADR-0002 D4) — the live serviceSummary() always
      // reports "unknown" in R0 too; there is no live health signal wired yet.
      health: 'unknown' as const,
      coverage: cov
        ? { wired: cov.wired, caveat: cov.caveat, gap: cov.gap, total: cov.total, ratio: cov.ratio }
        : { wired: 0, caveat: 0, gap: 0, total: 0, ratio: 0 },
      badges: [],
    }
  })

const TEAM_NAMES: Record<string, string> = {
  compute: 'Compute',
  'data-platform': 'Data Platform',
  storage: 'Storage',
  networking: 'Networking',
  security: 'Security',
  observability: 'Observability',
  'messaging-integration': 'Messaging & Integration',
  'platform-devex': 'Platform & DevEx',
  leadership: 'Leadership',
}

// ---------------------------------------------------------------------------
// Knowledge Base + SOPs (M8) — mirrors the real seed set (internal/sop/seed.go,
// internal/kb/seed.go): 3 example SOPs converted from real metalcloud
// runbooks, cited by source path. Every other service/cell is an honest gap,
// same as the live coverage-board math (internal/sop.Coverage).
// ---------------------------------------------------------------------------

export const FIXTURE_SOPS: Sop[] = [
  { slug: 'compute-vm-boot-failure', title: 'Compute — VM Boot Failure', cell: 'incident_response', services: ['compute'], riskClass: 'high', lifecycleState: 'draft', ownerTeam: 'compute', isSeed: true },
  { slug: 'compute-dr-metadata-restore', title: 'Compute — Disaster Recovery (Instance Metadata Restore)', cell: 'restore_dr', services: ['compute'], riskClass: 'high', lifecycleState: 'draft', ownerTeam: 'compute', isSeed: true },
  { slug: 'kms-service-unavailable', title: 'KMS — Service Unavailable', cell: 'incident_response', services: ['kms'], riskClass: 'high', lifecycleState: 'draft', ownerTeam: 'security', isSeed: true },
]

const FIXTURE_SOP_DETAILS: SopDetail[] = [
  {
    ...FIXTURE_SOPS[0],
    trigger: 'Firecracker microVM boot failure rate > 5% for 3m, or boot latency p99 > 5s. SEV1 — customers unable to create new VM instances.',
    preconditions: ['see-grant on compute (diagnosis) and do-grant on compute (remediation steps)'],
    steps: [
      { order: 1, title: 'Check host capacity', prose: 'List available compute hosts. If zero are available, capacity is exhausted — this is a capacity/quota issue, not a boot bug.', actionId: 'compute.hosts.list', params: [{ name: 'status', type: 'string', required: true, value: 'available' }] },
      { order: 2, title: 'Check image registry health', prose: 'Verify base images are accessible. Missing or corrupt base images prevent VM boot.', actionId: 'compute.images.health' },
      { order: 3, title: 'Diagnose Firecracker socket health', prose: 'Check jailer, rootfs mount, and vsock health on the affected host.', actionId: 'compute.host.diagnose', params: [{ name: 'host_id', type: 'string', required: true, value: '<host_id>', placeholder: true }] },
      { order: 4, title: 'Drain and restart the affected host', prose: 'Dry-run a drain of the affected host, then execute for real.', actionId: 'compute.host.drain', dryRunCapable: true, params: [{ name: 'host_id', type: 'string', required: true, value: '<host_id>', placeholder: true }, { name: 'dry_run', type: 'bool', required: true, value: 'true' }], rollback: { actionId: 'compute.host.undrain', params: [{ name: 'host_id', type: 'string', required: true, value: '<host_id>', placeholder: true }] } },
    ],
    rollback: 'Undrain any host drained during triage; disable burst capacity once the underlying capacity issue is resolved.',
    verification: ['compute health check returns OK', 'a test VM launches and terminates in under 1s (boot-test)', 'boot failure rate is below 0.01 (boot-metrics --metric failure_rate)'],
    sourcePaths: ['metalcloud:docs/runbooks/compute-boot-failure.md'],
  },
  {
    ...FIXTURE_SOPS[1],
    trigger: 'A declared DR event requiring restoration of compute instance metadata from a snapshot. Scope: instance metadata only — guest RAM/disk contents are out of scope by design (ADR-0432).',
    preconditions: ['A DR snapshot id exists', 'Operator authorization for a destructive restore'],
    steps: [
      { order: 1, title: 'Back up instance metadata', prose: 'Trigger a fleet-wide DR backup, which includes compute instance metadata records.', actionId: 'compute.dr.backup', params: [{ name: 'snapshot_id', type: 'string', required: true, value: '<snapshot_id>', placeholder: true }] },
      { order: 2, title: 'Restore instance metadata from a snapshot', prose: 'Restore all registered services from the named snapshot. Actual VMs are not running afterward and must be re-provisioned.', actionId: 'compute.dr.restore', params: [{ name: 'snapshot_id', type: 'string', required: true, value: '<snapshot_id>', placeholder: true }], note: 'MicroVMID/MicroVMIPv4 in restored records are stale.' },
    ],
    rollback: 'None — restore is the recovery action itself; a failed restore requires escalation, not an automatic undo.',
    verification: ['restored instance record GET returns 200 with identical name/machineType/zone/labels', 're-provisioned VMs boot successfully using the restored metadata as input'],
    sourcePaths: ['metalcloud:docs/services/compute/dr-runbook.md'],
  },
  {
    ...FIXTURE_SOPS[2],
    trigger: 'KMS health check failing for 2m, or encrypt-error rate > 5/s. SEV1 — cascading risk to IAM token signing and Secret Manager decryption.',
    preconditions: ['do-grant on kms for restart/failover actions'],
    steps: [
      { order: 1, title: 'Verify store connectivity', prose: 'KMS depends on the global-quorum store for key material; confirm the store itself is healthy before touching KMS.', actionId: 'store.health' },
      { order: 2, title: 'Check key material accessibility', prose: 'List active keys. An empty result means key material may be lost — escalate immediately.', actionId: 'kms.keys.list', params: [{ name: 'status', type: 'string', required: true, value: 'active' }] },
      { order: 3, title: 'Restart KMS in the affected region', prose: 'Dry-run a KMS restart in the affected region, then execute for real.', actionId: 'kms.restart', dryRunCapable: true, params: [{ name: 'region', type: 'string', required: true, value: '<region>', placeholder: true }, { name: 'dry_run', type: 'bool', required: true, value: 'true' }], rollback: { actionId: 'kms.restart', params: [{ name: 'region', type: 'string', required: true, value: '<region>', placeholder: true }] } },
    ],
    rollback: 'Restore the region via regioncoord.restore if a failover was performed and is no longer needed.',
    verification: ['kms health check returns OK', 'a test encrypt operation succeeds', 'iam health check returns OK (cascading dependency resolved)'],
    sourcePaths: ['metalcloud:docs/runbooks/kms-unavailable.md'],
  },
]

export const FIXTURE_SOP_BY_SLUG: Record<string, SopDetail> = Object.fromEntries(FIXTURE_SOP_DETAILS.map((s) => [s.slug, s]))

export const FIXTURE_KB_BY_SERVICE: Record<string, KBServiceDetail> = {
  compute: {
    service: 'compute',
    articles: [
      { id: 'compute-arch', kind: 'architecture', title: 'Compute (Forge) — Architecture & API Reference', body: 'Ground-up re-implementation of the Compute Engine v1 control plane. HTTP layer -> services/compute -> pkg/store (YugabyteDB) + Firecracker data plane.', sourcePath: 'metalcloud:docs/services/compute/README.md', updatedAt: '2026-07-06T12:00:00Z' },
      { id: 'compute-ops', kind: 'operating_manual', title: 'Compute (Forge) — Operator Runbook', body: 'Control plane is the single mcd binary. Persistent state lives in pkg/store. On-call rotation: forge-oncall.', sourcePath: 'metalcloud:docs/services/compute/runbook.md', updatedAt: '2026-07-06T12:00:00Z' },
      { id: 'compute-dr-link', kind: 'runbook_link', title: 'Compute — Disaster Recovery Runbook (ADR-0432)', url: 'docs/services/compute/dr-runbook.md', sourcePath: 'metalcloud:docs/services/compute/dr-runbook.md', updatedAt: '2026-07-06T12:00:00Z' },
    ],
    sops: [FIXTURE_SOPS[0], FIXTURE_SOPS[1]],
  },
  kms: {
    service: 'kms',
    articles: [
      { id: 'kms-arch', kind: 'architecture', title: 'KMS — Service Overview', body: 'Depends on the global-quorum store for key material; envelope encryption used by IAM and Secret Manager.', sourcePath: 'metalcloud:docs/services/kms/README.md', updatedAt: '2026-07-06T12:00:00Z' },
      { id: 'kms-ops', kind: 'operating_manual', title: 'KMS — Operator Runbook', body: 'Primary health check is kms health. HSM connectivity checked separately when hardware HSM is in use.', sourcePath: 'metalcloud:docs/services/kms/runbook.md', updatedAt: '2026-07-06T12:00:00Z' },
    ],
    sops: [FIXTURE_SOPS[2]],
  },
}

const MANDATORY_CELLS: Sop['cell'][] = ['restart', 'restore_dr', 'scale', 'incident_response', 'security_rotate']

export const FIXTURE_SOP_COVERAGE: SopCoverageRow[] = FIXTURE_SERVICES.map((svc) => ({
  service: svc.key,
  cells: MANDATORY_CELLS.map((cell) => {
    const match = FIXTURE_SOPS.find((s) => s.cell === cell && s.services.includes(svc.key))
    return match ? { cell, status: match.lifecycleState, sopSlug: match.slug, sopTitle: match.title } : { cell, status: 'gap' as const }
  }),
}))

// ---------------------------------------------------------------------------
// Alerting Management (M3) — a handful of representative alerts; compute's
// page alert carries a real sop ref (mirrors the engine's FR-ALR-02 rule:
// compute has a seeded incident_response SOP), cloudsql's does not (mirrors
// the engine downgrading a page candidate to warn when no runbook/SOP is
// attached — an honest gap, not a fabricated link).
// ---------------------------------------------------------------------------

export const FIXTURE_ALERTS: Alert[] = [
  {
    id: 'fixture-alert-1', service: 'compute', severity: 'page', title: 'compute is reporting down',
    source: 'rule', ruleKey: 'health-down', status: 'firing', owningTeam: 'compute', escalationTier: 0,
    sopSlug: 'compute-vm-boot-failure', hasRunbook: true, correlationGroup: 'fixture-group-1', occurrenceCount: 3,
    createdAt: '2026-07-06T11:40:00Z', updatedAt: '2026-07-06T11:55:00Z',
  },
  {
    id: 'fixture-alert-2', service: 'cloudsql', severity: 'warn', title: 'cloudsql is reporting degraded',
    note: 'downgraded from page: no incident-response SOP or runbook link found for this service',
    source: 'rule', ruleKey: 'health-degraded', status: 'acked', owningTeam: 'data-platform', escalationTier: 0,
    hasRunbook: false, correlationGroup: 'fixture-group-2', occurrenceCount: 1,
    ackedBy: 'person:fixture', ackedAt: '2026-07-06T10:05:00Z',
    createdAt: '2026-07-06T10:00:00Z', updatedAt: '2026-07-06T10:05:00Z',
  },
  {
    id: 'fixture-alert-3', service: 'kms', severity: 'page', title: 'kms error rate 8.0% is burning its 1.0% SLO target',
    source: 'burn', ruleKey: 'burn-rate', status: 'silenced', owningTeam: 'security', escalationTier: 0,
    sopSlug: 'kms-service-unavailable', hasRunbook: true, correlationGroup: 'fixture-group-3', occurrenceCount: 5,
    silenceReason: 'known noisy alert during planned key rotation', silencedBy: 'person:fixture',
    silencedAt: '2026-07-06T09:00:00Z', silencedUntil: '2026-07-06T21:00:00Z',
    createdAt: '2026-07-06T08:30:00Z', updatedAt: '2026-07-06T09:00:00Z',
  },
]

export const FIXTURE_ALERT_QUALITY: AlertQualityRow[] = [
  { team: 'compute', total: 12, noiseRatio: 0.25, actionability: 0.67, ackLatencySecondsAvg: 145 },
  { team: 'data-platform', total: 8, noiseRatio: 0.5, actionability: 0.5, ackLatencySecondsAvg: 320 },
  { team: 'security', total: 5, noiseRatio: 0.2, actionability: 0.8, ackLatencySecondsAvg: 90 },
  { team: 'observability', total: 0, noiseRatio: 0, actionability: 0, note: 'no alert history yet for this team' },
]

export const FIXTURE_SILENCES: Silence[] = FIXTURE_ALERTS.filter((a) => a.status === 'silenced').map((a) => ({
  alertId: a.id, service: a.service, severity: a.severity, title: a.title,
  reason: a.silenceReason ?? '', silencedBy: a.silencedBy ?? '', silencedAt: a.silencedAt ?? '', silencedUntil: a.silencedUntil ?? '',
}))

// Offline-dev-only sample for the notification center (the real feed is the
// current principal's own persisted notifications). A page-lane unread item +
// a digest-queued one, mirroring the two lanes the engine records.
export const FIXTURE_NOTIFICATIONS: Notification[] = [
  {
    id: 'fixture-notif-1', escalationId: 'fixture-esc-1', alertId: 'fixture-alert-1', service: 'compute',
    teamKey: 'compute', level: 0, severity: 'page', title: 'compute is reporting down',
    lane: 'page', read: false, createdAt: '2026-07-06T11:40:00Z',
  },
  {
    id: 'fixture-notif-2', escalationId: 'fixture-esc-2', alertId: 'fixture-alert-2', service: 'cloudsql',
    teamKey: 'data-platform', level: 0, severity: 'warn', title: 'cloudsql is reporting degraded',
    lane: 'digest', read: false, createdAt: '2026-07-06T10:00:00Z',
  },
]

// ---------------------------------------------------------------------------
// Incidents & Emergency Ops (M4) — a declared SEV1 (comms SLA not yet met, an
// honest breach flag) and a resolved SEV3 with its auto-drafted, submitted
// PIR — the same two-stage shape r1-incidents' store produces for real.
// ---------------------------------------------------------------------------

export const FIXTURE_INCIDENTS: Incident[] = [
  {
    id: 'fixture-incident-1', title: 'cloudsql is down', severity: 'SEV1', status: 'mitigating',
    commanderKind: 'person', commanderId: 'fixture-commander',
    responders: [{ kind: 'person', id: 'fixture-responder', addedAt: '2026-07-06T11:42:00Z' }],
    affectedServices: ['cloudsql'], linkedAlertIds: ['fixture-alert-2'],
    statuspageState: 'investigating', declaredByKind: 'person', declaredById: 'fixture-commander',
    declaredAt: '2026-07-06T11:40:00Z', commsSlaDeadline: '2026-07-06T11:55:00Z', commsSlaBreached: true,
    createdAt: '2026-07-06T11:40:00Z', updatedAt: '2026-07-06T11:42:00Z',
  },
  {
    id: 'fixture-incident-2', title: 'kms key rotation drill caused paging noise', severity: 'SEV3', status: 'resolved',
    commanderKind: 'person', commanderId: 'fixture-commander',
    responders: [], affectedServices: ['kms'], linkedAlertIds: ['fixture-alert-3'],
    statuspageState: 'resolved', declaredByKind: 'person', declaredById: 'fixture-commander',
    declaredAt: '2026-07-06T08:30:00Z', resolvedAt: '2026-07-06T09:10:00Z', commsSlaBreached: false,
    createdAt: '2026-07-06T08:30:00Z', updatedAt: '2026-07-06T09:10:00Z',
  },
]

/** buildFixtureIncidentDetail mirrors buildFixtureServiceDetail: a plausible timeline/PIR/health for one fixture incident, not a full replay of every store-side mutation. */
export function buildFixtureIncidentDetail(id: string): IncidentDetail {
  const base = FIXTURE_INCIDENTS.find((i) => i.id === id)
  if (!base) throw new Error(`fixture incident ${id} not found`)
  const timeline: TimelineEntry[] = [
    { id: 'tl-1', occurredAt: base.declaredAt, kind: 'declared', actor: 'person:fixture-commander', summary: `Incident declared: ${base.title} (${base.severity})` },
    { id: 'tl-2', occurredAt: base.declaredAt, kind: 'alert_linked', actor: 'person:fixture-commander', summary: 'Linked the seeded alert for this service' },
  ]
  if (base.status === 'resolved') {
    timeline.push({ id: 'tl-3', occurredAt: base.resolvedAt ?? base.updatedAt, kind: 'status_changed', actor: 'person:fixture-commander', summary: 'Status changed mitigating → resolved' })
  }
  const linkedAlertsDetail = FIXTURE_ALERTS.filter((a) => base.linkedAlertIds.includes(a.id))
  const affectedServiceHealth: ServiceHealthView[] = base.affectedServices.map((service) => ({
    service, health: 'unknown', source: { kind: 'fixtures-sample-data', observed_at: base.updatedAt, stale: false },
  }))
  return {
    ...base,
    timeline,
    linkedAlertsDetail,
    affectedServiceHealth,
    pir:
      base.status === 'resolved'
        ? {
            id: 'fixture-pir-1', incidentId: base.id, status: 'submitted',
            summary: 'Drill-induced paging noise from a planned key rotation.',
            rootCause: 'Rotation drill fired the burn-rate rule before the silence was in place.',
            impact: 'No customer impact; on-call paged unnecessarily for ~40 minutes.',
            actionItems: [{ id: 'fixture-action-item-1', description: 'Pre-silence the burn-rate rule before scheduled rotation drills', createdAt: base.updatedAt }],
            openedBy: 'person:fixture-commander', openedAt: base.updatedAt, submittedAt: base.updatedAt, updatedAt: base.updatedAt,
          }
        : undefined,
  }
}

export const FIXTURE_PEOPLE: OrgPerson[] = [
  { id: 'fixture-commander', name: 'Alex Rivera', email: 'alex.rivera@medhyai.internal', active: true },
  { id: 'fixture-responder', name: 'Jordan Kim', email: 'jordan.kim@medhyai.internal', active: true },
]

export const FIXTURE_AGENTS: OrgAgent[] = []

export const FIXTURE_SEARCH_INDEX: SearchResult[] = [
  ...FIXTURE_SERVICES.map((s) => ({ kind: 'service' as const, key: s.key, name: s.name })),
  ...Object.entries(TEAM_NAMES).map(([key, name]) => ({ kind: 'team' as const, key, name })),
  ...FIXTURE_SOPS.map((s) => ({ kind: 'sop' as const, key: s.slug, name: s.title })),
  ...Object.values(FIXTURE_KB_BY_SERVICE).flatMap((kb) => kb.articles.map((a) => ({ kind: 'kb' as const, key: kb.service, name: a.title }))),
]

// ---------------------------------------------------------------------------
// cloudsql — the hand-crafted exemplar service page, seeded from the real
// docs/design/services/cloudsql.md §2/§3/§4/§6 (evidence citations verbatim).
// Its wired/caveat/gap tally (13/9/19/41) matches docs/design/coverage.json exactly.
// ---------------------------------------------------------------------------

const CLOUDSQL_INVENTORY: InventoryItem[] = [
  { id: '1a', category: '1', categoryLabel: 'Golden signals', item: 'Golden signals — rate/errors, per verb, per plane', status: 'wired', rawStatus: 'wired', evidence: 'services/cloudsql/telemetry.go:13-61 (18 metrics, data plane); services/sql/telemetry.go:13-94 (~40 metrics, admin plane)' },
  { id: '1b', category: '1', categoryLabel: 'Golden signals', item: 'Golden signals — duration/latency, per verb', status: 'wired', rawStatus: 'wired', evidence: 'telemetry.go:82-103 / :198-219 — emit() wraps every call with Observe(latency)' },
  { id: '1c', category: '1', categoryLabel: 'Golden signals', item: 'Golden signals — saturation (CPU/mem/conn-pool/queue depth)', status: 'gap', rawStatus: 'gap', evidence: 'No such metric found in services/cloudsql, services/sql, or pkg/cloudsql. Only a static container resource cap (threat-model.md:66) — not an emitted signal.' },
  { id: '2a', category: '2', categoryLabel: 'Engine internals', item: 'Engine internals — connections', status: 'gap', rawStatus: 'gap', evidence: 'No pg_stat_activity-derived connection-count metric; the one hit (sqlexec/postgres.go:176-181) is used only to kill backends before DROP DATABASE.' },
  { id: '2b', category: '2', categoryLabel: 'Engine internals', item: 'Engine internals — replication lag', status: 'gap', rawStatus: 'gap', evidence: 'No pg_stat_replication/pg_replication_slots query anywhere. Capabilities() advertises SupportsHA/SupportsReadReplicas: true but no replica code path exists (limitations.md:68,241).' },
  { id: '2c', category: '2', categoryLabel: 'Engine internals', item: 'Engine internals — bloat', status: 'gap', rawStatus: 'gap', evidence: 'No pg_stat_user_tables/pgstattuple usage found.' },
  { id: '2d', category: '2', categoryLabel: 'Engine internals', item: 'Engine internals — WAL', status: 'gap', rawStatus: 'gap', evidence: 'No pg_current_wal_lsn/pg_stat_bgwriter usage found.' },
  { id: '2e', category: '2', categoryLabel: 'Engine internals', item: 'Engine internals — locks', status: 'gap', rawStatus: 'gap', evidence: 'No pg_locks usage found.' },
  { id: '2f', category: '2', categoryLabel: 'Engine internals', item: 'Engine internals — cache hit ratio', status: 'gap', rawStatus: 'gap', evidence: 'No pg_stat_database.blks_hit/blks_read usage found.' },
  { id: '2g', category: '2', categoryLabel: 'Engine internals', item: 'Engine health (boolean)', status: 'caveat', rawStatus: 'wired (partial)', evidence: 'HealthCheck() = bare SELECT 1 (engine_postgres/postgres.go:95-105); feeds SLI-3/SLI-4 in slo.md:13-20. The only engine-truth signal that exists today.' },
  { id: '3a', category: '3', categoryLabel: 'Logs & traces', item: 'Logs — structured, per-op', status: 'wired', rawStatus: 'wired', evidence: 's.rec.Info/Error with service, schema=v1, operation, outcome (telemetry.go:92-103 / :208-219).' },
  { id: '3b', category: '3', categoryLabel: 'Logs & traces', item: 'Logs — retention', status: 'gap', rawStatus: 'gap', evidence: 'No retention policy documented or found in code.' },
  { id: '3c', category: '3', categoryLabel: 'Logs & traces', item: 'Traces — per-op spans', status: 'wired', rawStatus: 'wired', evidence: 's.rec.StartSpan("cloudsql.<Op>"/"sql.<Op>") on every public method (cloudsql.go:126,174,205,228,248,287,318,341).' },
  { id: '3d', category: '3', categoryLabel: 'Logs & traces', item: 'Traces — exemplar linking (metric → trace)', status: 'gap', rawStatus: 'gap', evidence: 'No explicit exemplar mechanism; span and histogram share a code block only.' },
  { id: '3e', category: '3', categoryLabel: 'Logs & traces', item: 'Audit log (distinct from app logs)', status: 'caveat', rawStatus: 'wired (partial)', evidence: 'psgs-aw-12 middleware emits audit.Entry on 9 of 14 routes (mutating verbs only) — docs/services/cloudsql/audit/O4-audit-log.md:8-28.' },
  { id: '4a', category: '4', categoryLabel: 'Config surface', item: 'Config — engine selection (MC_CLOUDSQL_ENGINE, MC_POSTGRES_URL, MC_CLOUDSQL_PG_DSN, MC_CLOUDSQL_MYSQL_DSN)', status: 'wired', rawStatus: 'wired', evidence: 'services/cloudsql/config.go:11-23, factory.go:24-95, internal/server/server.go:1508-1519.' },
  { id: '4b', category: '4', categoryLabel: 'Config surface', item: 'Config — per-tenant rate limit (MC_RATELIMIT_*)', status: 'wired', rawStatus: 'wired', evidence: 'docs/services/cloudsql/quotas-and-limits.md:48-51 (platform-wide, not cloudsql-specific).' },
  { id: '4c', category: '4', categoryLabel: 'Config surface', item: 'Config — per-project quota (instances=10, databases=100)', status: 'caveat', rawStatus: 'wired (mostly)', evidence: 'Enforced per quotas-and-limits.md:26-31; exact quota.go:37,38 literal citation unconfirmed — existence of enforcement verified via RESOURCE_EXHAUSTED behavior.' },
  { id: '4d', category: '4', categoryLabel: 'Config surface', item: 'Config — everything else (users/instance, backups/instance, storage/instance, HA replicas)', status: 'gap', rawStatus: 'gap', evidence: 'Explicitly NOT_ENFORCED, tracked as DEBT-sql-quota-0001 (quotas-and-limits.md:17-31).' },
  { id: '4e', category: '4', categoryLabel: 'Config surface', item: 'Config — versioning/diff/drift detection', status: 'gap', rawStatus: 'gap', evidence: 'Plain env vars read once at boot (config.go:16-23); no schema version, no diff history.' },
  { id: '5a', category: '5', categoryLabel: 'Customer usage', item: 'Customer usage — per-tenant volume/API mix/errors seen', status: 'gap', rawStatus: 'gap', evidence: 'Metrics carry only an outcome label, no project label, despite multi-tenancy.md:44 claiming per-project histograms — doc overstates; code does not support this.' },
  { id: '5b', category: '5', categoryLabel: 'Customer usage', item: 'Customer usage — quota posture (query current usage)', status: 'wired', rawStatus: 'wired', evidence: 'GET /v1/projects/{p}/quota?metric=sql.instances (quotas-and-limits.md:100-101) — 2 dimensions only.' },
  { id: '5c', category: '5', categoryLabel: 'Customer usage', item: 'Customer usage — top-N tenants (by instance/database count)', status: 'caveat', rawStatus: 'wired (derivable, not a metric)', evidence: 'Not a metric, but real: ListInstances/admin-plane List return Project-tagged records aggregable client-side.' },
  { id: '5d', category: '5', categoryLabel: 'Customer usage', item: 'Customer usage — billing/volume proxy', status: 'caveat', rawStatus: 'wired (partial)', evidence: '3 create-event meters only (instances.create, .databases.create, .backups.create) — services/sql/billing_meter.go:13-20,53-78. No query/exec volume metering.' },
  { id: '6a', category: '6', categoryLabel: 'Security signals', item: 'Security — authz coverage', status: 'wired', rawStatus: 'wired', evidence: 'All 14 routes behind guardedProject(perm, h); cross-tenant probes return byte-identical 404s (multi-tenancy.md:67-78, brand.go:36-52).' },
  { id: '6b', category: '6', categoryLabel: 'Security signals', item: 'Security — cert/key issuance', status: 'caveat', rawStatus: 'wired (with a defect)', evidence: 'Real ECDSA P-256 + X.509 issuance (sql_ssl.go). Defect: claims "1 hour" TTL but hard-codes a 90-day NotAfter (sql_ssl.go:232,283-284).' },
  { id: '6c', category: '6', categoryLabel: 'Security signals', item: 'Security — server-CA cert', status: 'gap', rawStatus: 'gap', evidence: 'Wire field exists but is emitted empty (sql.go:324-339; limitations.md:92-93,108).' },
  { id: '6d', category: '6', categoryLabel: 'Security signals', item: 'Security — CMEK', status: 'gap', rawStatus: 'gap (placebo)', evidence: 'Format-validated only; never passed to the engine, no KMS existence check (sql.go:272-304; DEBT-l5-cmek-advertised-not-enforced).' },
  { id: '6e', category: '6', categoryLabel: 'Security signals', item: 'Security — CVE/posture scanning specific to cloudsql/Postgres', status: 'gap', rawStatus: 'gap', evidence: 'The generic services/securitybaseline module exists but has zero references to cloudsql.' },
  { id: '6f', category: '6', categoryLabel: 'Security signals', item: 'Security — anomaly/abuse canaries', status: 'caveat', rawStatus: 'wired (2 signals)', evidence: 'services/cloudsql/anomaly.go:172-208 — create_instance.error.spike (Warn) and delete_instance.spike (Error/immediate-page). A third canary is documented but not wired.' },
  { id: '6g', category: '6', categoryLabel: 'Security signals', item: 'Security — audit trail', status: 'wired', rawStatus: 'wired', evidence: 'See 3e.' },
  { id: '7a', category: '7', categoryLabel: 'Cost & capacity', item: 'Cost — rate card / unit pricing', status: 'gap', rawStatus: 'gap', evidence: 'No cost-model.md exists for cloudsql.' },
  { id: '7b', category: '7', categoryLabel: 'Cost & capacity', item: 'Cost — bench numbers', status: 'caveat', rawStatus: 'wired (but non-representative)', evidence: 'docs/strategy/baselines/cloudsql-baseline.json — explicitly labeled "backend": "in-process Go maps" — not representative of real Postgres-backed latency.' },
  { id: '7c', category: '7', categoryLabel: 'Cost & capacity', item: 'Cost — SC-tier / headroom / growth forecast', status: 'gap', rawStatus: 'gap', evidence: 'Not found anywhere.' },
  { id: '8a', category: '8', categoryLabel: 'Dependencies', item: 'Dependencies — upstream', status: 'wired', rawStatus: 'wired', evidence: 'pkg/store, pkg/telemetry, pkg/apierr, pkg/brand, pkg/aihook, services/resourcemanager, pkg/quota, pkg/cloudsql/engine (+ engine_postgres/memory), services/cloudsql/sqlexec → pgx/DuckDB/MySQL.' },
  { id: '8b', category: '8', categoryLabel: 'Dependencies', item: 'Dependencies — downstream / DR orchestrator', status: 'wired', rawStatus: 'wired', evidence: 'drOrch.Register("cloudsql", s.CloudSQL) (internal/server/server.go:1633) — participates in whole-platform DR.' },
  { id: '8c', category: '8', categoryLabel: 'Dependencies', item: 'Dependencies — real external engine', status: 'wired', rawStatus: 'wired', evidence: 'Pinned postgres:17-alpine image by digest (GOLD-CLOSEOUT.md:60); one container per tenant in production.' },
  { id: '9a', category: '9', categoryLabel: 'Backup & DR', item: 'Backup/DR — project export/import (row data)', status: 'wired', rawStatus: 'wired', evidence: 'services/cloudsql/cloudsql_dr.go:55-166; proven by TestCloudSQL_ExportImportProject_WithRows and the onboarding e2e G3 gate.' },
  { id: '9b', category: '9', categoryLabel: 'Backup & DR', item: 'Backup/DR — admin-plane backup row-data capture', status: 'caveat', rawStatus: 'wired (docs are stale)', evidence: 'Docs say admin-plane backups are metadata-only; internal/server/server.go:1519 shows s.SQLService.SetSQLExec(defExec) is now wired — docs predate this fix (last-verified 2026-06-16).' },
  { id: '9c', category: '9', categoryLabel: 'Backup & DR', item: 'Backup/DR — retention, scheduling, PITR, binlog replay', status: 'gap', rawStatus: 'gap', evidence: 'Explicitly not implemented (limitations.md:156-157).' },
  { id: '9d', category: '9', categoryLabel: 'Backup & DR', item: 'Backup/DR — last-drill tracking', status: 'gap', rawStatus: 'gap', evidence: 'No numeric RPO/last-drill artifact; only a single proven e2e test exists as evidence.' },
]

const CLOUDSQL_LENSES: Lens[] = [
  {
    name: 'Overview',
    panels: [
      'Instances (admin plane)',
      'Instances (data plane) — divergence flag vs admin plane',
      'Engine health — process reachability, not query health',
      'Error rate (5xx/internal)',
      'p99 latency',
      'Quota headroom',
      'Cross-tenant denial rate',
      'Coverage meter',
      'RED overlay (per plane, SLO thresholds drawn)',
      'Anomaly canary timeline',
      'Quota consumption trend (top-5 projects)',
      'Active alerts',
      'Recent mutating actions (audit stream)',
      'Top tenants by instance count',
      'Data-blindness banner — engine internals unobserved',
    ],
  },
  {
    name: 'Manage',
    panels: [
      'Identity header (name, engine, plane badges admin/data, owners, env)',
      'KPI strip',
      'Action panel — explicit plane selector (admin vs data, never merged)',
      'Config view — env-var surface, no diff/history (gap)',
    ],
  },
  {
    name: 'Scale',
    panels: [
      'Saturation/headroom — not instrumented (no CPU/conn-pool/queue-depth signal)',
      'Quota headroom — the only real scale signal',
      'SC-tier + growth forecast — not instrumented',
    ],
  },
  {
    name: 'Maintain',
    panels: [
      'Active alerts (anomaly canaries + burn-rate)',
      'Incident history',
      'Backup/DR status card (last export/import test result)',
      'Version-drift row (admin POSTGRES_15 vs data-plane POSTGRES_17)',
      'Dependency map',
    ],
  },
  {
    name: 'Secure',
    panels: [
      'Authz coverage (M12)',
      'Cert/key expiry — ephemeral-cert TTL defect flagged',
      'CMEK toggle — explicitly non-functional',
      'CVE/posture scan — not instrumented',
      'Abuse signals (2 anomaly canaries)',
      'Audit stream',
    ],
  },
  {
    name: 'Support',
    panels: [
      'Per-tenant usage/errors — not instrumented',
      'Quota-denial view (RESOURCE_EXHAUSTED outcome counts)',
      'Known issues (static links)',
    ],
  },
  {
    name: 'Learn',
    panels: ['Operating manual (KB-CSQL-01)', 'SOPs (replica promotion, connection-pool exhaustion)', 'Architecture note — two-plane split', 'PIR archive — none yet'],
  },
  {
    name: 'Enhance',
    panels: [
      'Debt list (dr dump/restore round-trip, pgdbname collision, orphaned engine DBs, version-vocabulary split)',
      'Cost trends — not instrumented',
      'Feature adoption — not instrumented',
      'Parity gaps — no replica/HA/PITR despite advertised capability',
    ],
  },
]

const CLOUDSQL_GAPS: string[] = [
  'PostgreSQL engine internals entirely unobserved — no connections, replication lag, bloat, WAL, locks, or cache-hit-ratio signal anywhere (§2a-2f). Highest-severity gap.',
  'No saturation/capacity signal for either the control-plane process or the engine (§1c).',
  'Per-tenant metric labeling does not exist, contradicting a claim in multi-tenancy.md (§5a).',
  'No CVE/security-posture module covers cloudsql/PostgreSQL specifically (§6e).',
  'No backup retention, scheduling, or PITR; no numeric last-drill tracking beyond one proven e2e test (§9c-9d).',
  'Config surface is not versioned — no diff/history mechanism exists (§4e).',
  'No cost model exists for cloudsql; bench numbers that do exist are non-representative in-memory-engine numbers (§7a-7c).',
  'Ephemeral SSL cert defect: documented 1-hour TTL, actual 90-day TTL — a correctness defect, not just an instrumentation gap (§6b).',
  'CMEK is a placebo — accepted and echoed, never enforced (§6d).',
  'Engine-version vocabulary mismatch between admin plane (POSTGRES_15) and data plane (POSTGRES_17) risks UI/operator confusion.',
  'Capability flags lie by omission — SupportsHA/SupportsPointInTimeRecovery/SupportsReadReplicas all advertise true with no backing code (§2b).',
  'Documentation staleness risk is systemic — verify gap claims against server.go wiring directly, not the docs’ gap tables.',
]

const GENERIC_LENS_PANELS: Record<string, string[]> = {
  Overview: ['Golden-signal KPI strip', 'Coverage meter', 'Top gaps banner'],
  Manage: ['Identity header', 'KPI strip', 'Action panel (Actions lane)', 'Config with diff/history'],
  Scale: ['Saturation + headroom', 'Load vs limits', 'Scaling controls', 'SC-tier + blockers', 'Forecast'],
  Maintain: ['Alerts', 'Incident history', 'Backup/DR status + last drill', 'Patch/upgrade queue', 'Dependency map'],
  Secure: ['Posture row', 'Cert/secret expiry', 'CVEs', 'Abuse signals', 'Authz coverage'],
  Support: ['Per-tenant usage/errors', 'Affected-tenants view', 'Quota denials', 'Known issues'],
  Learn: ['KB/manual', 'SOPs', 'Architecture', 'PIR archive'],
  Enhance: ['Debt list', 'Perf/cost trends', 'Feature adoption', 'Parity gaps'],
}

/** Generic categories, from docs/design/01-service-page-template.md Part A. */
const CATEGORIES: Array<{ num: string; label: string }> = [
  { num: '1', label: 'Golden signals' },
  { num: '2', label: 'Engine internals' },
  { num: '3', label: 'Logs & traces' },
  { num: '4', label: 'Config surface' },
  { num: '5', label: 'Customer usage' },
  { num: '6', label: 'Security signals' },
  { num: '7', label: 'Cost & capacity' },
  { num: '8', label: 'Dependencies' },
  { num: '9', label: 'Backup & DR' },
]

/**
 * A data-driven, non-hardcoded fallback descriptor for the 49 services other
 * than cloudsql: the counts match this service's real docs/design/coverage.json
 * tally exactly, proving the service-page framework renders from `inventory` +
 * `lenses` rather than per-service branching. Item text is a labeled sample —
 * the live /admin/v2/services/{svc} spec is the real per-item citation source.
 */
function buildGenericInventory(key: string, wired: number, caveat: number, gap: number): InventoryItem[] {
  const statuses: Array<'wired' | 'caveat' | 'gap'> = [
    ...Array(wired).fill('wired' as const),
    ...Array(caveat).fill('caveat' as const),
    ...Array(gap).fill('gap' as const),
  ]
  return statuses.map((status, i) => {
    const cat = CATEGORIES[i % CATEGORIES.length]
    const n = Math.floor(i / CATEGORIES.length) + 1
    return {
      id: `${cat.num}-${n}`,
      category: cat.num,
      categoryLabel: cat.label,
      item: `${cat.label} — item ${n}`,
      status,
      rawStatus: status,
      evidence:
        status === 'gap'
          ? `sample fixture — no live citation (see docs/design/services/${key}.md for the real evidence)`
          : `sample fixture — live evidence ships from docs/design/services/${key}.md`,
    }
  })
}

export function buildFixtureServiceDetail(key: string): ServiceDetail {
  const summary = FIXTURE_SERVICES.find((s) => s.key === key)
  if (!summary) {
    throw new Error(`fixtures: unknown service ${key}`)
  }
  if (key === 'cloudsql') {
    return {
      ...summary,
      description:
        'Structurally two services wearing one name: an admin plane (services/sql) and a data plane (services/cloudsql), split documented at docs/services/cloudsql/limitations.md:16-44.',
      sourceFile: 'docs/design/services/cloudsql.md',
      inventory: CLOUDSQL_INVENTORY,
      lenses: CLOUDSQL_LENSES,
      gaps: CLOUDSQL_GAPS,
    }
  }
  const { wired, caveat, gap } = summary.coverage
  return {
    ...summary,
    description: `${summary.name} — sample fixture descriptor (offline dev only; the live spec is docs/design/services/${key}.md).`,
    sourceFile: `docs/design/services/${key}.md`,
    inventory: buildGenericInventory(key, wired, caveat, gap),
    lenses: Object.entries(GENERIC_LENS_PANELS).map(([name, panels]) => ({ name, panels })),
    gaps: [`Sample fixture — the real §6 gap list ships from the live /admin/v2/services/${key} spec.`],
  }
}

// ---------------------------------------------------------------------------
// Home dashboard sample fixtures were DELETED (real-integration-1): the CEO
// Home dashboard now composes from GET /admin/v2/leadership/summary (real
// fleet health / SLO / incidents / alerts / security), and the still-unbacked
// journeys + tenant-movements widgets render an honest "not wired yet" state
// instead of fabricated rows. See api/client.ts fetchHomeDashboard.
// ---------------------------------------------------------------------------

/** The CEO/leadership persona (r0-authn, FR-ORG-04) — offline-dev stand-in for a real session. */
export const FIXTURE_WHOAMI: WhoAmIEnvelope = {
  principal: {
    kind: 'person',
    email: 'founder@medhyai.internal',
    name: 'Founder',
    roles: ['ceo'],
    teams: ['leadership'],
    seesAllServices: true,
  },
}

